import { motion } from 'framer-motion';
import { SearchX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/Domain/components/Shared/Button/Button';

interface DomainUnreachableNoticeProps {
  urlHost: string;
  onCorrect: () => void;
  onSwitchToManual: () => void;
}

const containerVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
};

/**
 * Shown when the client-side DoH check finds the typed domain does not exist
 * (definitive NXDOMAIN). There is no "proceed anyway": the scrape cannot succeed
 * against a host that does not resolve, and 15% of users were taking that path
 * into a 60-150s backend failure. Fixing the address or setting up manually are
 * the only two outcomes that can work.
 */
export const DomainUnreachableNotice = ({
  urlHost,
  onCorrect,
  onSwitchToManual,
}: DomainUnreachableNoticeProps) => {
  const { t } = useTranslation('getting_started');

  return (
    <motion.div
      layout
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="flex flex-col gap-6 bg-card border border-border rounded-2xl p-6"
      data-testid="gs-domain-unreachable-notice"
      role="alertdialog"
      aria-labelledby="gs-domain-unreachable-notice-title"
    >
      <div className="flex flex-col gap-4">
        {/* Soft amber anchor: a "couldn't find" caution, not a hard error. */}
        <span className="flex size-11 items-center justify-center rounded-2xl bg-warning-muted">
          <SearchX className="size-5 text-warning" />
        </span>
        <div className="flex flex-col gap-1.5">
          <h2
            id="gs-domain-unreachable-notice-title"
            className="text-xl font-semibold tracking-tight text-foreground text-balance"
          >
            {t('domain_unreachable_notice.title', { host: urlHost })}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('domain_unreachable_notice.description', { host: urlHost })}
          </p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('domain_unreachable_notice.no_proceed_hint')}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Button
          type="button"
          size="md"
          onClick={onCorrect}
          data-testid="gs-domain-unreachable-notice-correct"
          className="w-full"
        >
          {t('domain_unreachable_notice.correct_cta')}
        </Button>

        <div className="flex items-center justify-center gap-5 pt-0.5">
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={onSwitchToManual}
            data-testid="gs-domain-unreachable-notice-manual"
            className="!text-muted-foreground hover:!text-foreground"
          >
            {t('domain_unreachable_notice.secondary_cta_manual_setup')}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
